import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Errors } from '../common/msg';
import { showAmount } from '../../libs/util';
import CurrencySelect from '../../components/form/currencySelect';

import { SelectField, useValidation, NumberField } from '../../components/form';
import useInterval from '../../libs/useInterval';
import { upload } from '../../libs/api';

const FileUpload = ({
  onFilesChange,
  maxFiles = 5,
  acceptedTypes = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.mp4,.avi,.mov,.wmv,.flv,.webm',
}) => {
  const [files, setFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFiles = (newFiles) => {
    const fileArray = Array.from(newFiles);
    const validFiles = fileArray.filter((file) => {
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      return acceptedTypes
        .split(',')
        .some((type) => type.trim() === fileExtension);
    });

    if (files.length + validFiles.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const updatedFiles = [...files, ...validFiles];
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const removeFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleFileSelect = (e) => {
    handleFiles(e.target.files);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileTypeDisplay = (file) => {
    const extension = file.name.split('.').pop().toLowerCase();

    if (file.type.startsWith('video/')) {
      return { icon: '🎥', color: '#e85347', text: 'VIDEO' };
    }

    switch (extension) {
      case 'xls':
      case 'xlsx':
        return { icon: '📊', color: '#217346', text: 'XLS' };
      case 'pdf':
        return { icon: '📄', color: '#e85347', text: 'PDF' };
      case 'doc':
      case 'docx':
        return { icon: '📝', color: '#2b579a', text: 'DOC' };
      case 'jpg':
      case 'jpeg':
      case 'png':
        return { icon: '🖼️', color: '#6f42c1', text: 'IMG' };
      default:
        return { icon: '📎', color: '#526484', text: extension.toUpperCase() };
    }
  };

  return (
    <div className="form-group">
      <label className="form-label">Supporting Documents</label>
      <div className="form-note">Upload supporting documents (optional)</div>

      {/* Drag & Drop Area */}
      <div
        className={`file-upload-area ${isDragOver ? 'drag-over' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${isDragOver ? '#526484' : '#e3e7fe'}`,
          borderRadius: '6px',
          padding: '2rem',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragOver ? '#f7f9fc' : '#fbfcfe',
          transition: 'all 0.3s ease',
          marginBottom: '1rem',
        }}
      >
        <div
          style={{
            color: '#526484',
            fontSize: '1.1rem',
            marginBottom: '0.5rem',
          }}
        >
          📁 Drag & drop files here
        </div>
        <div style={{ color: '#8094ae', fontSize: '0.875rem' }}>
          or click to browse (PDF, JPG, PNG, DOC, Excel, MP4, AVI, MOV)
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedTypes}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Selected Files */}
      {files.length > 0 && (
        <div className="selected-files-section">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '0.75rem',
            }}
          >
            <h6
              style={{
                margin: 0,
                color: '#526484',
                fontSize: '0.875rem',
                fontWeight: '600',
              }}
            >
              Selected Files
            </h6>
            <span
              style={{
                backgroundColor: '#e3e7fe',
                color: '#526484',
                padding: '0.25rem 0.5rem',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: '500',
              }}
            >
              {files.length} file{files.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="selected-files-list">
            {files.map((file, index) => {
              const fileDisplay = getFileTypeDisplay(file);
              return (
                <div
                  key={index}
                  className="selected-file-item"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    backgroundColor: '#f7f9fc',
                    border: '1px solid #e3e7fe',
                    borderRadius: '6px',
                    marginBottom: '0.5rem',
                  }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', flex: 1 }}
                  >
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: fileDisplay.color,
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '0.75rem',
                        fontSize: '0.75rem',
                        color: 'white',
                        fontWeight: '600',
                      }}
                    >
                      {fileDisplay.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          color: '#364a63',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {file.name}
                      </div>
                      <div style={{ color: '#8094ae', fontSize: '0.75rem' }}>
                        {formatFileSize(file.size)}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#e85347',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      borderRadius: '4px',
                      fontSize: '1.25rem',
                      lineHeight: 1,
                      marginLeft: '0.5rem',
                    }}
                    title="Remove file"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const Component = ({
  whitelists,
  wallets,
  setWithdrawalAmount,
  resetFee,
  fee,
  submitting,
  onSubmit,
}) => {
  const REFRESH_INTERVAL = 2;
  const CONFIRM_INTERVAL = 30; //sec

  const DECIMAL_USDC = '6'; //sec
  const DECIMAL_BTC = '8'; //sec

  const [amount, setAmount] = useState('');
  const [addressOptions, setAddressOptions] = useState([]);
  const [filteredAddressOptions, setFilteredAddressOptions] = useState([]);
  const [address, setAddress] = useState();
  const [whitelist, setWhitelist] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [errors, setErrors] = useState([]);
  const [showError, setShowError] = useState(false);
  const [setFieldValid, isValid] = useValidation();
  const [selectedCurrency, setSelectedCurrency] = useState('USDT');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const WITHDRAWAL_FILE_CATEGORY = 14;

  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [currencySelection, setCurrencySelection] = useState('');
  const [enableWhitelist, setEnableWhitelist] = useState(true);

  const [availableCurrencies, setAvailableCurrencies] = useState([]);

  useEffect(() => {
    if (whitelists && whitelists.length > 0) {
      const currencies = [
        ...new Set(whitelists.map((white) => white.currency.toUpperCase())),
      ];
      const currencyOptions = currencies.map((currency) => ({
        value: currency,
        label: currency,
      }));
      setAvailableCurrencies(currencyOptions);
    }
  }, [whitelists]);

  useEffect(() => {
    if (whitelists) {
      let list = whitelists.map((white) => ({
        value: white.whitelist_id,
        label:
          white.network === 'FIAT'
            ? `${white.beneficiary_name} - ${white.vasp} - ${white.bank.bankName} - ${white.bank.swiftCode} - ${white.currency} (${white.network})`
            : `${white.note} - ${white.address} - ${white.currency} (${white.network})`,
        currency: white.currency.toUpperCase(),
      }));
      setAddressOptions(list);
    }
  }, [whitelists]);

  useEffect(() => {
    if (currencySelection && addressOptions.length > 0) {
      const filtered = addressOptions.filter(
        (option) => option.currency === currencySelection.toUpperCase()
      );
      setFilteredAddressOptions(filtered);
    } else {
      setFilteredAddressOptions(addressOptions);
    }

    setAddress('');
    setWhitelist(null);
    setAmount('');
    resetFee();
  }, [currencySelection, addressOptions]);

  const getWhitelist = (id) => {
    for (const white of whitelists) {
      if (white.whitelist_id == id) {
        return white;
      }
    }
    return null;
  };

  const fetchFee = () => {
    if ((wallets && whitelist && amount) || amount > 0.0) {
      setWithdrawalAmount(whitelist, wallet, amount);
    }
  };

  useEffect(() => {
    setWhitelist(getWhitelist(address));
    setAmount('');
    resetFee();
  }, [address]);

  // Requote on wallet change too: the fee follows the wallet's currency, so switching
  // the amount-box currency must refetch or the figure shown belongs to the old wallet.
  useEffect(() => {
    resetFee();
    setWithdrawalAmount(whitelist, wallet, amount);
  }, [amount, wallet]);

  useInterval(() => {
    if (!wallets || !whitelist || !amount || amount <= 0.0) {
      resetFee();
      return;
    } else {
      //at intervals check fee
      if (countdown <= 0) {
        fetchFee();
        setCountdown(CONFIRM_INTERVAL);
      } else setCountdown(countdown - 1);
    }
  }, 1000);

  useEffect(() => {
    if (whitelist) {
      setSelectedCurrency(whitelist?.currency.toUpperCase());
    }
  }, [whitelist]);

  useEffect(() => {
    if (wallets && whitelist) {
      if (whitelist.currency.toUpperCase === 'BTC') {
        const BTCWallet = wallets.find(
          (x) => x.currency.toUpperCase() === 'BTC'
        );
        setWallet(BTCWallet);
      } else {
        const selectedWallet = wallets.find(
          (x) => x.currency.toUpperCase() === selectedCurrency.toUpperCase()
        );
        setWallet(selectedWallet);
      }
    }
  }, [selectedCurrency, wallets, whitelist]);

  const handleSubmit = async (e) => {
    if (!isValid()) {
      setShowError(true);
      return;
    }

    if (!wallet) {
      setErrors(['No wallet found.']);
      return;
    }
    if (parseFloat(amount) > wallet.main_balance) {
      setErrors(['Exceed wallet balances.']);
      return;
    }

    // Amount-vs-fee is enforced by /wallet/withdrawal/validate against the WALLET's fee.
    // The old check here used whitelist.fee - a different asset whenever the amount-box
    // currency differs from the address label.

    setErrors([]);

    // Upload files if any are selected
    let uploadedFileIds = [];
    if (uploadedFiles.length > 0) {
      setUploadingFiles(true);
      try {
        const uploadPromises = uploadedFiles.map(async (file) => {
          const uploadData = await upload(
            '/wallet/file/upload',
            file,
            true,
            WITHDRAWAL_FILE_CATEGORY
          );
          if (uploadData?.status !== 200) {
            throw new Error('Error uploading file');
          }
          return uploadData.data.file_id;
        });

        uploadedFileIds = await Promise.all(uploadPromises);
        console.log('All files uploaded successfully:', uploadedFileIds);
      } catch (error) {
        setUploadingFiles(false);
        setErrors(['File upload failed. Please try again.']);
        return;
      } finally {
        setUploadingFiles(false);
      }
    }

    if (onSubmit) {
      onSubmit(getWhitelist(address), wallet, amount, uploadedFileIds);
    }
  };

  const handleCurrencyChange = (e) => {
    setSelectedCurrency(e.target.value);
  };

  const handleFilesChange = (files) => {
    setUploadedFiles(files);
  };

  const selectCurrency = (value) => {
    if (value) {
      setCurrencySelection(value);
      setEnableWhitelist(false);
    }
  };

  return (
    <>
      {errors.length > 0 && <Errors errors={errors} />}
      <div className="form-validate is-alter mt-4 form-authentic">
        <div className="form-group">
          <label className="form-label" htmlFor="current-password">
            Currency <span className="text-danger">*</span>
          </label>
          <div className="form-control-wrap">
            <SelectField
              className="form-control"
              placeholder="Please select currency"
              required={true}
              showError={showError}
              onValidate={(valid) => setFieldValid('currency', valid)}
              options={availableCurrencies}
              value={currencySelection}
              onChange={(e) => selectCurrency(e.target.value)}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="current-password">
            Whitelist Address <span className="text-danger">*</span>
          </label>
          <div className="form-control-wrap">
            <SelectField
              className="form-control"
              placeholder="Please select"
              required={true}
              showError={showError}
              onValidate={(valid) => setFieldValid('address', valid)}
              options={filteredAddressOptions}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              selectDisabled={enableWhitelist}
            />
            <Link
              to="/portal/wallet/whitelist/add"
              className="btn btn-primary mt-2"
            >
              <span>Add Withdrawal Address</span>
            </Link>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="amount">
            Amount <span className="text-danger">*</span>
          </label>
          <div className="form-control-wrap">
            <div className="form-info">
              {whitelist?.currency.toUpperCase() === 'BTC' ? (
                'BTC'
              ) : whitelist?.currency.toUpperCase() === 'USD' ? (
                'USD'
              ) : whitelist?.currency.toUpperCase() === 'SGD' ||
                whitelist?.currency.toUpperCase() === 'JPY' ? (
                <p>{whitelist?.currency.toUpperCase()}</p>
              ) : (
                <CurrencySelect
                  value={selectedCurrency}
                  onChange={handleCurrencyChange}
                />
              )}
            </div>

            <NumberField
              className="form-control form-control-lg form-control-number bg-white"
              placeholder="Enter Amount"
              value={amount}
              required={true}
              showError={showError}
              onValidate={(valid) => setFieldValid('amount', valid)}
              onChange={(e) => setAmount(e.target.value)}
              decimalPlaces={
                wallet?.currency.toUpperCase() == 'USDC' ||
                wallet?.currency.toUpperCase() == 'USDT'
                  ? DECIMAL_USDC
                  : DECIMAL_BTC
              }
            />
          </div>
          <div className="form-note-group">
            {wallet && (
              <>
                <span className="form-note-alt">
                  <a href="#" onClick={(e) => setAmount(wallet.main_balance)}>
                    Wallet Balance:{' '}
                    {showAmount(
                      wallet.main_balance,
                      wallet.currency,
                      null,
                      true
                    )}
                  </a>
                  &nbsp;
                  <button
                    className="btn btn-xs btn-primary"
                    onClick={(e) => setAmount(wallet.main_balance)}
                  >
                    MAX
                  </button>
                </span>
              </>
            )}
          </div>
        </div>

        {/* File Upload Component with Excel support */}
        <FileUpload
          onFilesChange={handleFilesChange}
          maxFiles={5}
          acceptedTypes=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.mp4,.avi,.mov,.wmv,.flv,.webm"
        />

        {wallet && (
          <div className="form-group">
            <div>
              <label className="form-label" htmlFor="current-password">
                Available Balance: {showAmount(wallet.main_balance)}
              </label>
            </div>
            <div>
              <label className="form-label" htmlFor="current-password">
                {/* The quote is denominated in the wallet's currency, so label it with
                    the wallet - not the whitelist, which is only the address's label. */}
                Fee: {showAmount(fee)} {wallet.currency.toUpperCase()}{' '}
                (Deducted)
              </label>
              &nbsp;
            </div>
          </div>
        )}

        {whitelist && (
          <div className="form-note-group">
            <span className="form-note-alt iv-invest-fixed">
              <em>
                Warning: Please send your {whitelist.currency} coins only on the{' '}
                {whitelist.network} network with the correct address. Sending it
                on a wrong network will result in the permanent loss of funds.
              </em>
            </span>
          </div>
        )}

        <div className="nk-pps-field form-action mt-4">
          <div className="nk-pps-action">
            <button
              className="btn btn-lg btn-block btn-primary"
              onClick={handleSubmit}
              disabled={!amount || amount <= 0.0 || uploadingFiles}
            >
              <span>
                {uploadingFiles
                  ? 'Uploading Files...'
                  : 'Submit Withdrawal Request'}
              </span>
              {(submitting || uploadingFiles) && (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                ></span>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Component;
